/**
 * 田字格：**一格一个字**，头顶标拼音，中间是虚线十字。
 *
 * ## 为什么是一个组件而不是各页面自己画
 *
 * 田字格的"格子比例 + 中线位置 + 字在格子里的垂直位置"三件事必须处处一致：
 * 一旦哪个页面自己写一遍，字就会在某一页里偏上/偏左（而家长看到的是"字写歪了"，
 * 不是"CSS 写错了"）。所以这里只暴露三种尺寸与一个"选中"状态，几何全部收在这里。
 *
 * ## 只有 CSS + 少量标记（不依赖任何库）
 *
 * 外框是 `border`，中间的十字是**两条绝对定位的虚线边**（`border-dashed`）。
 * 刻意不画"米字格"的斜线：小学听写用的是田字格，多两条斜线会让 48px 的小格子
 * 变成一团乱线。
 *
 * ## 窄屏优先
 *
 * 格子是**固定尺寸的正方形**（不伸缩），一行放不下就整行换行（调用方用
 * `flex flex-wrap`）—— 绝不做成"挤一挤变窄"：田字格一变形，字就不是方块了。
 *
 * ⚠️ 内部一律用 `<span>` + `block/flex` 而不是 `<div>`：这个格子常常整个是
 *    **按钮内容**，而 `<button>` 只允许 phrasing content（`<div>` 在里面是非法 HTML，
 *    浏览器会把它甩出去）。
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { pinyinLabel } from '../../lib/dictationPreview';

/** 格子尺寸。`sm` 给词卡上的字，`lg` 给笔顺动画那一块大的。 */
export type TianZiGeSize = 'sm' | 'md' | 'lg';

/** 格子的边长（**正方形，不伸缩**：一变形字就不是方块了）。 */
const FRAME: Record<TianZiGeSize, string> = {
  sm: 'h-12 w-12',
  md: 'h-14 w-14',
  lg: 'h-44 w-44',
};

/**
 * 格子里的字号。
 *
 * 与边长的比例是刻意定的：字占格子约 2/3 宽（小学练字要求"字写在格子中间、
 * 四周留白"），所以 48/56px 的格子配 `text-2xl`/`text-3xl`，176px 的大格子配 `text-8xl`。
 *
 * 目前 `lg` 只被**笔顺动画**那一块用到（那里格子里装的是 Hanzi Writer 画的 SVG，
 * 不是字体画的字），但字号一并定义在这里：尺寸刻度只该有一份，
 * 否则将来想加一个"大字格"就会有人自己现编一个数字。
 */
const TEXT: Record<TianZiGeSize, string> = {
  sm: 'text-2xl',
  md: 'text-3xl',
  lg: 'text-8xl',
};

/**
 * 田字格的**外框 + 中线**（不含内容）。
 *
 * 内容由 `children` 决定：字（`TianZiGeChar`）或 Hanzi Writer 画的 SVG
 * （`StrokeOrderCard`）。两条中线画在最底层（`z-0`），内容在 `z-10` 之上。
 */
export function TianZiGeFrame({
  size = 'md',
  selected = false,
  className,
  children,
}: {
  size?: TianZiGeSize;
  /** 选中态：正在看这个字的笔顺。 */
  selected?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'relative block shrink-0 overflow-hidden rounded-lg border bg-white',
        FRAME[size],
        selected ? 'border-2 border-brand-500' : 'border-slate-300',
        className,
      )}
    >
      {/* 中线：田字格的两条虚线（不是实线，实线会与笔画抢注意力） */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
        <span className="absolute inset-y-0 left-1/2 border-l border-dashed border-rose-300" />
        <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-rose-300" />
      </span>
      <span className="relative z-10 block h-full w-full">{children}</span>
    </span>
  );
}

/**
 * 一格字（可点：点了看它的笔顺）。
 *
 * 拼音在上、字在下。**拿不到读音时拼音位显示「—」而不是留空** ——
 * 留空在界面上看起来像排版坏了，而「—」是"这里本该有个读音、只是没拿到"。
 */
export function TianZiGeChar({
  char,
  pinyin,
  size = 'md',
  selected = false,
  onClick,
}: {
  char: string;
  /** `null` = 拿不到读音（显示「—」）。 */
  pinyin: string | null;
  size?: TianZiGeSize;
  selected?: boolean;
  /** 给了就是可点的格子（按钮），不给就是纯展示。 */
  onClick?: () => void;
}) {
  const cell = (
    <TianZiGeFrame size={size} selected={selected}>
      <span
        className={cn(
          // 楷体优先：语文课本上的字形是楷体，宋体/黑体在"笔画起收"上看着不一样
          'tianzige-char flex h-full w-full items-center justify-center leading-none text-slate-900',
          TEXT[size],
        )}
      >
        {char}
      </span>
    </TianZiGeFrame>
  );

  return (
    <span className="flex flex-col items-center gap-0.5">
      <span
        className={cn(
          'block max-w-full truncate font-mono text-[11px] leading-4',
          // 「—」比真读音浅一档，但仍然看得见：家长要能一眼看出"这里缺了读音"
          pinyin === null ? 'text-slate-400' : 'text-slate-500',
        )}
        title={pinyin === null ? '这个字的读音没拿到' : pinyin}
        aria-hidden="true"
      >
        {pinyinLabel(pinyin)}
      </span>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          aria-pressed={selected}
          aria-label={`看「${char}」${pinyin === null ? '' : `（${pinyin}）`}的笔顺`}
          className="rounded-lg focus-visible:outline-none"
        >
          {cell}
        </button>
      ) : (
        cell
      )}
    </span>
  );
}
